import { MultiStepFormState } from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form-item';
import {
  SubmitHandlerWithDirtyFields,
  buildDirtyObject,
} from '@delmonicos/shared-design-system-react/src/contexts/multi-step-form.provider';
import { removeEmptyFields } from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/remove-empty-fields/remove-empty-fields';
import { getUpdateTokenDefaultValues } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/token/update-token.form-default-values';
import { UpdateTokenModel } from '@delmonicos/shared-sdk/libs/cpms/src/libs/charge-hub-service-lib/src/wrapper/inputs/token/update-token.model';
import { useState } from 'react';
import { SubmitErrorHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { Logger } from '@app/core/logger/logger';
import { TokenUpdateFormController } from '@app/feature/token/token-update/token-update-form/token-update-form.controller';
import {
  UpdateTokenContextProvider,
  useTokenContext,
} from '@app/feature/token/token.context';
import { useCpmsBoUpdateTokenMutation } from '@service/cpms-bo-token.service';

interface TokenUpdateControllerProps {
  onCancel: () => void;
  onSuccess: () => void;
}

export const TokenUpdateController = ({
  onCancel,
  onSuccess,
}: TokenUpdateControllerProps): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { t } = useTranslation();
  const [updateTokenMutation] = useCpmsBoUpdateTokenMutation();
  const tokenToUpdate = useTokenContext();
  const [multiStepFormState, setMultiStepFormState] = useState(
    MultiStepFormState.Default,
  );

  // HANDLERS //////////////////////////////////////////////////////////////////

  const handleError: SubmitErrorHandler<
    Omit<UpdateTokenModel, 'customerId' | 'networkId'>
  > = (errors) => {
    Logger.error(errors);
    toast.error(t('$.token-update.error'));
  };

  const handleSubmit: SubmitHandlerWithDirtyFields<
    Omit<UpdateTokenModel, 'customerId' | 'networkId'>
  > = (fieldValues, _, dirtyFields) => {
    setMultiStepFormState(MultiStepFormState.Loading);
    const sanitizedObject = removeEmptyFields(
      buildDirtyObject(dirtyFields, fieldValues),
    );

    if (!sanitizedObject.token) {
      setMultiStepFormState(MultiStepFormState.Default);
      return;
    }

    const updateToken = {
      tokenId: fieldValues.tokenId,
      token: sanitizedObject.token,
    };

    updateTokenMutation(updateToken)
      .unwrap()
      .then(() => {
        toast.success(t('$.token-update.success'));
        onSuccess();
      })
      .catch((error) => {
        Logger.error(error);
        toast.error(t('$.token-update.error', { error: t('global.unknown') }));
      })
      .finally(() => setMultiStepFormState(MultiStepFormState.Default));
  };

  // RENDER //////////////////////////////////////////////////////////////////

  const data = getUpdateTokenDefaultValues(tokenToUpdate);

  return (
    <UpdateTokenContextProvider data={data}>
      <TokenUpdateFormController
        onError={handleError}
        onSubmit={handleSubmit}
        onCancel={onCancel}
        state={multiStepFormState}
      />
    </UpdateTokenContextProvider>
  );
};
